import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient from '../../api/apiClient';
import KPIHeader from '../../components/hris/kpi/KPIHeader';
import KPIStats from '../../components/hris/kpi/KPIStats';
import KPICards from '../../components/hris/kpi/KPICards';
import KPIModal from '../../components/hris/kpi/KPIModal';
import DeleteKPIDialog from '../../components/hris/kpi/DeleteKPIDialog';
import { containerVariants, itemVariants } from '../../utils/animations';

const initialForm = {
  name: '',
  department_id: '',
  description: '',
  target_value: '',
  unit: '%',
  weight: '',
  period: 'monthly',
  status: 'active',
};

export default function KPIList() {
  const [kpis, setKpis] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDept, setFilterDept] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKpi, setEditingKpi] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await apiClient.get('/hris/kpi');
      setKpis(response.data?.kpis || []);
      setDepartments(response.data?.departments || []);
    } catch (error) {
      // Error loading data
    } finally {
      setLoading(false);
    }
  };

  const getDepartmentName = deptId =>
    departments.find(d => d.id === deptId)?.name || 'Semua Departemen';

  const openDialog = (kpi = null) => {
    if (kpi) {
      setEditingKpi(kpi);
      setFormData({
        name: kpi.name || '',
        department_id: kpi.department_id ? String(kpi.department_id) : '',
        description: kpi.description || '',
        target_value: kpi.target_value?.toString() || '',
        unit: kpi.unit || '%',
        weight: kpi.weight?.toString() || '',
        period: kpi.period || 'monthly',
        status: kpi.status || 'active',
      });
    } else {
      setEditingKpi(null);
      setFormData(initialForm);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const data = {
        ...formData,
        department_id: formData.department_id ? Number(formData.department_id) : null,
        target_value: formData.target_value ? parseFloat(formData.target_value) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
      };
      if (editingKpi) {
        await apiClient.put(`/hris/kpi/${editingKpi.id}`, data);
      } else {
        await apiClient.post('/hris/kpi', data);
      }
      loadData();
      setDialogOpen(false);
    } catch (error) {
      // Error saving KPI
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await apiClient.delete(`/hris/kpi/${deleteId}`);
      loadData();
      setDeleteId(null);
    } catch (error) {
      // Error deleting KPI
    }
  };

  const filteredKpis = kpis.filter(kpi => filterDept === 'all' || kpi.department_id === filterDept);
  const totalWeight = filteredKpis.reduce((sum, kpi) => sum + (kpi.weight || 0), 0);

  return (
    <motion.div
      className="space-y-4 md:space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants}>
        <KPIHeader onAdd={() => openDialog()} />
      </motion.div>
      <motion.div variants={itemVariants}>
        <KPIStats
          totalWeight={totalWeight}
          filterDept={filterDept}
          onFilterChange={setFilterDept}
          departments={departments}
        />
      </motion.div>
      <motion.div variants={itemVariants}>
        <KPICards
          kpis={filteredKpis}
          loading={loading}
          getDepartmentName={getDepartmentName}
          onEdit={openDialog}
          onDelete={setDeleteId}
          onAdd={() => openDialog()}
        />
      </motion.div>
      <KPIModal
        open={dialogOpen}
        form={formData}
        setForm={setFormData}
        departments={departments}
        editing={!!editingKpi}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onSave={handleSubmit}
      />
      <DeleteKPIDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
      />
    </motion.div>
  );
}
